import { useEffect, useState } from 'react';
import { Button, Offcanvas } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import type { VendorCustomerType, VendorType } from '@econtractor/types';
import * as apiVendors from 'api/apiVendors';
import { useModal } from 'context/ModalContext';
import { getButtonsNoYesDanger } from 'context/helpers/helperModalContext';
import { useToast } from 'context/ToastContext';
import ESpinner from 'components/others/Spinner';
import SolarIcon from 'components/icons/SolarIcon';
import SvgTrash from 'components/svg/SvgTrash';
import OffVendorCustomerEdit from './OffVendorCustomerEdit';
import VendorCustomersTable from './VendorCustomersTable';

interface OffVendorCustomersProps {
    vendorId: number | null;
    vendor?: VendorType;
    onHide: () => void;
}

const OffVendorCustomers = ({ vendorId, vendor, onHide }: OffVendorCustomersProps) => {
    const { t } = useTranslation(['Vendors', 'common']);
    const { showToast } = useToast();
    const { showDlg } = useModal();
    const [list, setList] = useState<VendorCustomerType[] | null>(null);
    const [showSpinner, setShowSpinner] = useState(false);
    const [editCustomer, setEditCustomer] = useState<VendorCustomerType | null>(null);
    const [isCreate, setIsCreate] = useState(false);
    const [formNonce, setFormNonce] = useState(0);

    const show = vendorId !== null;

    const fetchList = async () => {
        if (!vendorId) return;
        const res = await apiVendors.getVendorCustomers(vendorId);
        setShowSpinner(false);
        if (!res) {
            showToast(t('common:messError'), 'danger');
            setList((prev) => prev ?? []);
            return;
        }
        setList(res);
    };

    const handleClose = () => {
        setIsCreate(false);
        setEditCustomer(null);
        setList(null);
        onHide();
    };

    const handleHideEdit = () => {
        setIsCreate(false);
        setEditCustomer(null);
    };

    const handleDelete = (row: VendorCustomerType) => {
        const buttons = [...getButtonsNoYesDanger(t)];
        buttons[1].handleClick = async () => {
            showDlg(false);
            if (!vendorId) return;
            setShowSpinner(true);
            const stat = await apiVendors.deleteVendorCustomer(vendorId, row.ID);
            await fetchList();
            setShowSpinner(false);
            if (stat) showToast(t('subjects.messDeleted'), 'success');
            else showToast(t('common:messError'), 'danger');
        };

        showDlg({
            type: 'confirm',
            buttons,
            text: (
                <>
                    <h4 className="d-flex pb-2 mb-2 border-bottom text-danger">
                        <SvgTrash className="text-danger" />
                        <span className="ps-2">{t('subjects.confirmDelete')}</span>
                    </h4>
                    <h5 className="mb-0">{row.fullName || row.edrpouOrIpn}</h5>
                    <small className="form-text text-muted">{row.edrpouOrIpn}</small>
                </>
            ),
        });
    };

    useEffect(() => {
        if (!vendorId) return;
        setList(null);
        setIsCreate(false);
        setEditCustomer(null);
        void fetchList();
    }, [vendorId]);

    return (
        <>
            <Offcanvas show={show} onHide={handleClose} placement="end" className="off-vendor-customers">
                <Offcanvas.Header className="border-bottom" closeButton>
                    <Offcanvas.Title className="me-auto">
                        {t('subjects.title')}
                        {vendor?.vendorName ? (
                            <span className="text-muted fs-6 d-block fw-normal">{vendor.vendorName}</span>
                        ) : null}
                    </Offcanvas.Title>
                </Offcanvas.Header>
                <Offcanvas.Body className="position-relative pb-0 d-flex flex-column px-0">
                    <div className="off-coming-form d-flex flex-column h-100">
                        <div className="off-coming-content px-3 pb-3">
                            {list == null ? (
                                <ESpinner />
                            ) : (
                                <>
                                    <VendorCustomersTable
                                        rows={list}
                                        showSpinner={showSpinner}
                                        onEdit={(row) => {
                                            setIsCreate(false);
                                            setFormNonce((n) => n + 1);
                                            setEditCustomer(row);
                                        }}
                                        onDelete={handleDelete}
                                    />
                                    <div className="d-flex justify-content-center mt-3">
                                        <Button
                                            type="button"
                                            variant="info"
                                            size="sm"
                                            className="me-2"
                                            onClick={() => {
                                                setEditCustomer(null);
                                                setFormNonce((n) => n + 1);
                                                setIsCreate(true);
                                            }}
                                        >
                                            <SolarIcon name="plus" className="me-1" />
                                            {t('subjects.toolAdd')}
                                        </Button>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </Offcanvas.Body>
            </Offcanvas>

            {vendorId ? (
                <OffVendorCustomerEdit
                    vendorId={vendorId}
                    customer={editCustomer}
                    isCreate={isCreate}
                    formNonce={formNonce}
                    onHide={handleHideEdit}
                    onSaved={async () => {
                        setShowSpinner(true);
                        await fetchList();
                    }}
                />
            ) : null}
        </>
    );
};

export default OffVendorCustomers;
